import { PropsWithChildren, useCallback, useEffect, useId } from 'react'
import { useFormContext } from 'react-hook-form'
import { enqueueSnackbar } from 'notistack'
import { useTracking } from '../../../hooks/tracking'
import { getErrorMessages } from '../../../utils/error-mapping'
import { Button, Stack } from '@mui/material'
import { RouteLeavingGuard } from '../../../components/route-leaving-guard'
import { useTranslation } from 'react-i18next'
import { PageHeaderPanel } from '../../../components/page-header-panel'
import { stripNullValues } from '../../../utils/strip-null-values'

type Props<T> = PropsWithChildren & {
  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  onSubmit(form: T): Promise<T | void>
  serverData?: T
  submitLabel?: string
  disabled?: boolean
  hasLeavingGuard?: boolean
  successMessage?: string
  isLoading: boolean
}

export const PluginsForm = <T extends Record<string, unknown>>(
  props: Props<T>,
) => {
  const { hasLeavingGuard = true, serverData, successMessage } = props
  const { t } = useTranslation(['plugins'])
  const formId = useId()

  const { formState, handleSubmit, reset } = useFormContext<T>()

  const { isDirty, isSubmitSuccessful } = formState

  const { trackButtonClick, trackFormSuccess, trackFormError } = useTracking()

  const resetForm = useCallback(
    (data: T) => {
      reset(stripNullValues(data) as T, { keepDefaultValues: false })
    },
    [reset],
  )

  useEffect(() => {
    if (serverData) resetForm(serverData)
  }, [reset, resetForm, serverData])

  const onSubmit = async (formData: T) => {
    trackButtonClick({ name: 'plugins-form-save' })

    try {
      const serverData = await props.onSubmit(formData)

      if (serverData) resetForm(serverData)

      trackFormSuccess({ name: 'plugins-form-success' })

      enqueueSnackbar(successMessage ?? t('plugins:success_edit'))
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : getErrorMessages(error).join(', ')

      trackFormError({ name: 'plugins-form-error', errorMessage })

      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)} noValidate>
      {hasLeavingGuard && (
        <RouteLeavingGuard when={isDirty && !isSubmitSuccessful} />
      )}

      <PageHeaderPanel>
        <Stack direction="row" spacing={1} justifyContent="end" sx={{ py: 1 }}>
          <Button
            form={formId}
            type="submit"
            disabled={props.disabled ?? !formState.isDirty}
            size="small"
            loading={props.isLoading}
          >
            {props.submitLabel || t('shared:action.save')}
          </Button>
        </Stack>
      </PageHeaderPanel>

      {props.children}
    </form>
  )
}
